import React from 'react';

import DataConnector from 'connect/DataConnector';
import {EntitySummary} from 'constants/UserConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';

/**
 * The data connector is private to this module. We use it silently, with no loading indicators.
 * It simply fetches what's requested, and returns the map of `entitySummariesByHandle`
 */
interface EntitySummariesByHandleConnectorInputProps {
  handles: Array<string>;
  children: (entitySummariesByHandle: {[id: string]: EntitySummary}) => JSX.Element;
}

interface EnhancedProps {
  entitySummariesByHandle: {[id: string]: EntitySummary};
}

class EntitySummariesByHandleConnector extends DataConnector<
  EntitySummariesByHandleConnectorInputProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    return {
      entitySummariesByHandle: this.getOrFetchMultipleOptionalDeprecated(
        ManagedModelType.ENTITY_SUMMARY_BY_HANDLE,
        this.props.handles
      ),
    };
  }
}

/**
 * A small but useful utility function is provided to consumers.
 */
export function getEntityDisplayName(
  entitySummariesByHandle: {[id: string]: EntitySummary},
  handle: string
): string {
  const entityData = entitySummariesByHandle[handle];
  if (entityData) {
    return entityData.name || handle || '';
  }
  return handle || '';
}

/**
 * The public API in this module is a component that uses a render prop to retrieve entities:
 *
 *     <EntitySummariesByHandleFetcher handles={componentProps.handles}>
 *       {(entitySummariesByHandle, getEntityDisplayName) => ()}
 *     </EntitySummariesByHandleFetcher>
 *
 * As this uses a DataConnector under the hood, with an "optional" query, the resulting
 * `entitySummariesByHandle` map may be incomplete. I.e., the handles you requested may not exist
 * in the map. You are responsible for handling this case.
 *
 */
interface EntitySummariesByHandleFetcherProps {
  handles: Array<string>;
  children: (entitySummariesByHandle: {[handle: string]: EntitySummary}) => JSX.Element;
}

class EntitySummariesByHandleFetcherRenderer extends React.Component<
  EntitySummariesByHandleFetcherProps & EnhancedProps
> {
  override render(): JSX.Element {
    return this.props.children(this.props.entitySummariesByHandle);
  }
}

export const EntitySummariesByHandleFetcher = EntitySummariesByHandleConnector.connect(
  EntitySummariesByHandleFetcherRenderer
);
